import { quickJsonFetch } from '../BusinessObjects/AlacrityInterfaces';
import { IPayloadWebResponse } from '../BusinessObjects/AlacrityInterfaces';
import { PayloadWebRequest } from '../BusinessObjects/AlacrityInterfaces';
import { BaseIdentifyingInformation } from '../BusinessObjects/AlacrityInterfaces';
import {
  IDriversLicenseLookupValueMap,
  DriversLicenseLookupValueFieldType,
  ILookupCodeTranslation,
} from '../DriversLicenseLookupOptions';
import {
  IDriversLicenseLookupResult,
  IDriversLicenseLookupResultValue,
} from '../BusinessObjects/DriversLicenseLookupResponse';

import Api from '../../../../../services/Api';
import { ApiResponse } from 'apisauce';
import { toast } from 'react-toastify';
import { ComponentUtils } from '../../../AutoComplete/lib/business-objects/ComponentUtils';

export namespace DriversLicenseLookupUtils {
  export const DriversLicenseSearch = async (url: string): Promise<any> => {
    return Api.get(url)
      .then((response: ApiResponse<any>) => {
        if (response.status === 204) {
          toast.warn('Driver license validation returned no records.');
          return null;
        }

        if (!response.ok) {
          const errorMessage = response.data?.message || response.data?.error || '';
          const statusText = response.status ? ` (${response.status})` : '';
          const message = errorMessage
            ? `Driver license validation failed${statusText}: ${errorMessage}`
            : `Driver license validation service call failed${statusText}.`;
          toast.warn(message);
          return null;
        }

        const data = response.data;
        if (!data) {
          toast.warn('Driver license validation returned no data.');
          return null;
        }

        if (data.success === false || data.Success === false) {
          const message = data.error || data.Error || 'Driver license validation returned no records.';
          toast.warn(message);
          return null;
        }

        return data;
      })
      .catch((error) => {
        console.log('driver license lookup error', error);
        toast.warn('Driver license validation request failed before reaching the API.');
        return null;
      });
  };

  export const ProcessDriversLicense = async (
    data: IDriversLicenseLookupResult,
    component: any,
    form: any
  ) => {
    if (!form || !data) {
      return;
    }

    const components: IDriversLicenseLookupValueMap[] =
      component.compMaps || [];
    const normalizedData = normalizeDriversLicenseResponse(data);

    for (let i = 0; i < components.length; i++) {
      const valueMap: IDriversLicenseLookupValueMap = components[i];
      const lookupResultValue: IDriversLicenseLookupResultValue =
        getNestedObject(normalizedData, valueMap.lookupFieldName.split('.'));
      if (lookupResultValue === undefined || lookupResultValue === null) continue;

      mapValueToComponent(lookupResultValue, valueMap, form, component);
    }
  };

  const mapValueToComponent = (
    lookupResultValue: any,
    valueMap: IDriversLicenseLookupValueMap,
    form: any,
    licensePlateComponent: any
  ) => {
    const changeComponent = findMappedComponent(form, valueMap.modelFieldName);

    if (!changeComponent) return;

    var componentValue: any = lookupResultValue;
    if (
      valueMap.lookupFieldType == DriversLicenseLookupValueFieldType.Code &&
      typeof lookupResultValue === 'object'
    ) {
      componentValue = getPropertyCaseInsensitive(lookupResultValue, 'valueId');
    }

    if (componentValue === undefined || componentValue === null) {
      return;
    }

    if (
      valueMap.lookupFieldName.toLowerCase() == 'driverdateofbirth' &&
      typeof componentValue === 'string' &&
      componentValue.length > 5
    ) {
      componentValue =
        componentValue.split(' ')[0].replace(/\//g, '-').split('-')[2] +
        '-' +
        componentValue.split(' ')[0].replace(/\//g, '-').split('-')[0] +
        '-' +
        componentValue.split(' ')[0].replace(/\//g, '-').split('-')[1];
    }

    if (
      componentValue &&
      valueMap.codeTranslations &&
      valueMap.codeTranslations.length > 0
    ) {
      const trans: ILookupCodeTranslation | undefined = valueMap.codeTranslations.find(
        (translation: ILookupCodeTranslation) =>
          translation.lookupValue == componentValue
      );

      componentValue = trans && trans.outputValue ? trans.outputValue : '';
    }

    if (
      !changeComponent.getValue() ||
      licensePlateComponent.overrideExistingValues
    ) {
      changeComponent.setValue(componentValue);
    }
  };

  const findMappedComponent = (form: any, componentPath: string): any => {
    if (!form || !componentPath) {
      return null;
    }

    if (componentPath.includes('\\')) {
      return ComponentUtils.getComponent(form, componentPath);
    }

    let parent = form.parent;
    while (parent && parent !== form.root) {
      if (typeof parent.getComponent === 'function') {
        const component = parent.getComponent(componentPath);
        if (component) {
          return component;
        }
      }

      parent = parent.parent;
    }

    return form.root && typeof form.root.getComponent === 'function'
      ? form.root.getComponent(componentPath)
      : null;
  };

  export const normalizeDriversLicenseResponse = (data: any): any => {
    const result = getPropertyCaseInsensitive(data, 'result');
    const response = result && typeof result === 'object' ? result : data;
    const records = getPropertyCaseInsensitive(response, 'records');

    if (!Array.isArray(records) || records.length === 0) {
      return response;
    }

    const record = records[0];
    const fullName = getPropertyCaseInsensitive(record, 'fullName') || '';
    const nameParts = String(fullName).split(',').map(value => value.trim());
    const state = getPropertyCaseInsensitive(record, 'st');

    return {
      ...response,
      success: true,
      driverLastName: nameParts[0] || '',
      driverFirstName: nameParts[1] || '',
      driverMiddleName: nameParts[2] || '',
      driverAddress: getPropertyCaseInsensitive(record, 'address'),
      driverCity: getPropertyCaseInsensitive(record, 'city'),
      driverState: state,
      driverZip: getPropertyCaseInsensitive(record, 'zip'),
      driverDateOfBirth: getPropertyCaseInsensitive(record, 'birthDate'),
      driverGender: getPropertyCaseInsensitive(record, 'sex'),
      dlnState: state,
    };
  };

  const getPropertyCaseInsensitive = (value: any, propertyName: string): any => {
    if (value === undefined || value === null || typeof value !== 'object') {
      return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(value, propertyName)) {
      return value[propertyName];
    }

    const matchingProperty = Object.keys(value).find(
      key => key.toLowerCase() === propertyName.toLowerCase()
    );
    return matchingProperty === undefined ? undefined : value[matchingProperty];
  };

  const getNestedObject = (nestedObj, pathArr) => {
    return pathArr.reduce(
      (obj, key) => getPropertyCaseInsensitive(obj, key),
      nestedObj
    );
  };
}
